import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users2, RefreshCw, FileDown, FileSpreadsheet, CheckCircle2, PauseCircle } from "lucide-react";

/**
 * Card único do quadro: 10 indicadores + mini breakdown gráfico.
 * Substitui o antigo ContractBreakdownCard.
 * Consome /api/dashboard/workforce-indicators.
 */
const FORMA_COLORS = {
  CLT: "#234935", PJ: "#5B7FBF", Estagio: "#D97706",
  Aprendiz: "#7C3AED", Temporario: "#0891B2",
  Terceiro: "#8B5CF6", Autonomo: "#65A30D", Outros: "#94A3B8",
};

const nf = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

export default function WorkforceIndicatorsCard({
  companyId, sectorId, title = "Composição do Quadro por Forma de Contratação",
  testId = "workforce-indicators", compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    const p = new URLSearchParams();
    if (companyId && companyId !== "all") p.set("company_id", companyId);
    if (sectorId) p.set("sector_id", sectorId);
    try {
      const { data: d } = await api.get(`/dashboard/workforce-indicators?${p.toString()}`);
      setData(d);
    } catch {
      setData({ total: 0, ativos: 0, afastados: 0, por_forma: [] });
    } finally { setLoading(false); setReloading(false); }
  };

  useEffect(() => { load(); }, [companyId, sectorId]);

  const downloadExport = async (format) => {
    const params = new URLSearchParams();
    if (companyId && companyId !== "all") params.set("company_id", companyId);
    if (sectorId) params.set("sector_id", sectorId);
    params.set("status", "ativo");
    const url = `${API_BASE}/dashboard/contract-breakdown/export.${format}?${params.toString()}`;
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("Falha no download");
      const blob = await resp.blob();
      const bUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bUrl;
      a.download = `composicao_vinculo_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      window.URL.revokeObjectURL(bUrl);
    } catch (ex) { toast.error(ex.message || "Falha ao exportar"); }
  };

  if (loading) return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`${testId}-loading`}>
      <div className="text-[13px] text-muted-foreground">Carregando indicadores...</div>
    </div>
  );

  const total = data?.total || 0;
  const forma = data?.por_forma || [];
  const cards = [
    ...forma.map((f) => ({ key: f.key, label: f.label, count: f.count, pct: f.pct,
                            color: FORMA_COLORS[f.key] || FORMA_COLORS.Outros })),
    { key: "ativos", label: "Ativos", count: data?.ativos || 0,
      pct: total ? Math.round((data.ativos / total) * 100 * 10) / 10 : 0,
      color: "#059669", accent: true, icon: CheckCircle2 },
    { key: "afastados", label: "Afastados", count: data?.afastados || 0,
      pct: total ? Math.round((data.afastados / total) * 100 * 10) / 10 : 0,
      color: "#F59E0B", accent: true, icon: PauseCircle },
    { key: "total", label: "Total Geral", count: total, pct: 100,
      color: "#234935", accent: true, big: true },
  ];

  return (
    <div className="rounded-xl border border-border bg-white p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#234935]/10 grid place-items-center">
            <Users2 className="h-4 w-4 text-[#234935]" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Quadro por Vínculo</div>
            <div className="font-display text-lg sm:text-xl font-semibold leading-tight">{title}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 px-2 text-[11.5px]" onClick={() => downloadExport("xlsx")} data-testid={`${testId}-export-xlsx`}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-[11.5px]" onClick={() => downloadExport("pdf")} data-testid={`${testId}-export-pdf`}>
            <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={load} disabled={reloading} data-testid={`${testId}-refresh`}>
            <RefreshCw className={`h-3.5 w-3.5 ${reloading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Indicadores */}
      <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10"}`}>
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.key}
              className={`rounded-lg ring-1 px-3 py-2.5 ${c.accent ? "text-white" : "bg-white ring-slate-200"}`}
              style={c.accent ? { background: c.color, borderColor: c.color } : { borderColor: c.color + "40" }}
              data-testid={`${testId}-${c.key.toLowerCase()}`}
              title={`${c.label}: ${nf(c.count)} (${c.pct}%)`}
            >
              <div className="flex items-center gap-1.5">
                {Icon
                  ? <Icon className="h-3 w-3 opacity-90" />
                  : <span className={`h-1.5 w-1.5 rounded-full`} style={{ background: c.accent ? "#fff" : c.color }} />}
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${c.accent ? "opacity-90" : ""}`}
                      style={c.accent ? {} : { color: c.color }}>
                  {c.label}
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <span className={`${c.big ? "text-2xl" : "text-lg"} font-bold tabular-nums`}
                      style={c.accent ? {} : { color: c.color }}>{nf(c.count)}</span>
                {c.pct > 0 && <span className="text-[10.5px] opacity-70">{c.pct}%</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mini breakdown gráfico (barra segmentada) */}
      {total > 0 && (
        <div className="mt-4" data-testid={`${testId}-chart`}>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Distribuição por forma de contratação</div>
          <div className="flex h-3 w-full rounded-full overflow-hidden bg-slate-100">
            {forma.filter((f) => f.count > 0).map((f) => (
              <div key={f.key} className="h-full transition-all"
                   style={{ width: `${f.pct}%`, background: FORMA_COLORS[f.key] || FORMA_COLORS.Outros }}
                   title={`${f.label}: ${nf(f.count)} (${f.pct}%)`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
            {forma.filter((f) => f.count > 0).map((f) => (
              <div key={f.key} className="flex items-center gap-1.5 text-[11px]">
                <span className="h-1.5 w-1.5 rounded-full"
                      style={{ background: FORMA_COLORS[f.key] || FORMA_COLORS.Outros }} />
                <span className="text-muted-foreground">{f.label} · <b className="text-foreground">{nf(f.count)}</b> ({f.pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
